import React, { useState, useRef, useEffect } from 'react';
import { marked } from 'marked';
import type { RoomLayout } from '../../types.ts';
import { useArchitectChat } from '../hooks/useArchitectChat.ts';
import type { Skill } from '../types.ts';

// Helper to parse markdown synchronously into HTML string
function renderMarkdown(text: string) {
  const html = marked.parse(text, { breaks: true, gfm: true }) as string;
  return { __html: html };
}

interface RoomArchitectChatProps {
  backendUrl: string;
  roomData: RoomLayout;
  skills: Skill[];
  onApplyPromptToOrchestrator: (promptText: string) => void;
  onRunOrchestrator: (promptText: string) => void;
  onOpenSkillsLibrary?: () => void;
}

export function RoomArchitectChat({
  backendUrl,
  roomData,
  skills,
  onApplyPromptToOrchestrator,
  onRunOrchestrator,
  onOpenSkillsLibrary
}: RoomArchitectChatProps) {
  const [inputText, setInputText] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const activeSkillsCount = skills.filter(s => s.enabled).length;

  const {
    messages,
    selectedModel,
    setSelectedModel,
    availableModels,
    isLoading,
    errorMessage,
    sendMessage,
    clearChat
  } = useArchitectChat({
    backendUrl,
    roomData,
    onApplyPromptToOrchestrator: (promptText) => {
      onApplyPromptToOrchestrator(promptText);
    }
  });

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = () => {
    if (!inputText.trim() || isLoading) return;
    const text = inputText;
    setInputText('');
    const activeSkillIds = skills.filter(s => s.enabled).map(s => s.id);
    sendMessage(text, activeSkillIds);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={cardStyle}>
      {/* Dynamic Keyframe Animations & Markdown Formatting Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes pulseGlow {
          0% { box-shadow: 0 0 8px rgba(156, 39, 176, 0.3); }
          50% { box-shadow: 0 0 20px rgba(156, 39, 176, 0.7); }
          100% { box-shadow: 0 0 8px rgba(156, 39, 176, 0.3); }
        }
        @keyframes chatDotBounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1.0); }
        }
        .markdown-chat-content p {
          margin: 0 0 8px 0;
        }
        .markdown-chat-content p:last-child {
          margin-bottom: 0;
        }
        .markdown-chat-content strong, .markdown-chat-content b {
          font-weight: 700;
          color: #ffffff;
        }
        .markdown-chat-content ul, .markdown-chat-content ol {
          margin: 6px 0 8px 0;
          padding-left: 20px;
        }
        .markdown-chat-content li {
          margin-bottom: 4px;
        }
        .markdown-chat-content li:last-child {
          margin-bottom: 0;
        }
        .markdown-chat-content code {
          background-color: rgba(0, 0, 0, 0.3);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 12px;
        }
        .markdown-chat-content pre {
          background-color: #121212;
          padding: 10px;
          border-radius: 6px;
          overflow-x: auto;
          margin: 8px 0;
          border: 1px solid #333;
        }
        .markdown-chat-content pre code {
          background: none;
          padding: 0;
        }
        .markdown-chat-content h1, .markdown-chat-content h2, .markdown-chat-content h3, .markdown-chat-content h4 {
          margin: 10px 0 6px 0;
          font-weight: 600;
          line-height: 1.3;
        }
        .markdown-chat-content h1:first-child, .markdown-chat-content h2:first-child, .markdown-chat-content h3:first-child, .markdown-chat-content h4:first-child {
          margin-top: 0;
        }
      `}} />

      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={avatarStyle}>🏛️</div>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#f3f4f6' }}>
              Architekt Místnosti (AI Chat)
            </h3>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
              {activeSkillsCount} aktivních skillů
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onOpenSkillsLibrary && (
            <button
              onClick={onOpenSkillsLibrary}
              style={skillsBtnStyle}
              title="Otevřít Knihovnu Skillů (Agent Skills Library)"
            >
              🧠 Agent Skills Library
            </button>
          )}
          <button
            onClick={clearChat}
            style={clearBtnStyle}
            title="Resetovat konverzaci"
          >
            🗑️ Reset
          </button>
        </div>
      </div>

      {/* Model Selector Sub-header */}
      <div style={modelSelectorContainerStyle}>
        <label style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>
          AI Model:
        </label>
        <select
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          style={modelSelectStyle}
        >
          {availableModels.map(m => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      {/* Messages Scroll View */}
      <div ref={messagesContainerRef} style={messagesScrollStyle}>
        {messages.map(msg => {
          const isUser = msg.sender === 'user';
          return (
            <div
              key={msg.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'flex-end' : 'flex-start',
                marginBottom: '14px'
              }}
            >
              <div style={{
                fontSize: '11px',
                color: '#9ca3af',
                marginBottom: '4px',
                padding: '0 4px'
              }}>
                {isUser ? 'Vy' : 'Architekt Místnosti'} • {msg.timestamp}
              </div>

              <div style={isUser ? userBubbleStyle : architectBubbleStyle}>
                <div
                  className="markdown-chat-content"
                  style={{ lineHeight: '1.5' }}
                  dangerouslySetInnerHTML={renderMarkdown(msg.text)}
                />

                {/* Consulted Agent Skills Badges */}
                {!isUser && msg.usedSkills && msg.usedSkills.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '10px',
                    paddingTop: '8px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)'
                  }}>
                    <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500 }}>
                      🧠 Konsultované skilly ({msg.usedSkills.length}):
                    </span>
                    {msg.usedSkills.map(s => (
                      <span
                        key={s.id}
                        title={s.description}
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: 'rgba(156, 39, 176, 0.15)',
                          color: '#e9d5ff',
                          border: '1px solid rgba(156, 39, 176, 0.3)',
                          borderRadius: '12px',
                          padding: '2px 8px'
                        }}
                      >
                        ✓ {s.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Orchestrator handoff card */}
                {msg.action === 'EXECUTE_ORCHESTRATOR' && msg.targetTextareaValue && (
                  <div style={handoffCardStyle}>
                    <div style={handoffHeaderStyle}>
                      <span>✨ Připravená instrukce pro Orchestrátor:</span>
                    </div>
                    <div style={handoffPromptBoxStyle}>
                      {msg.targetTextareaValue}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button
                        onClick={() => {
                          onApplyPromptToOrchestrator(msg.targetTextareaValue!);
                          onRunOrchestrator(msg.targetTextareaValue!);
                        }}
                        style={runOrchestratorBtnStyle}
                      >
                        🚀 Spustit Orchestrátor Nábytku
                      </button>

                      <button
                        onClick={() => {
                          onApplyPromptToOrchestrator(msg.targetTextareaValue!);
                        }}
                        style={copyPromptBtnStyle}
                      >
                        ✍️ Pouze vložit do Textarea
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Loading Indicator */}
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px' }}>
            <div style={architectBubbleStyle}>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#9c27b0' }}>Architekt přemýšlí</span>
                <span style={dotStyle(0)}>•</span>
                <span style={dotStyle(200)}>•</span>
                <span style={dotStyle(400)}>•</span>
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div style={errorStyle}>
            Chyba: {errorMessage}
          </div>
        )}

      </div>

      {/* Input controls */}
      <div style={inputContainerStyle}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Napište zprávu pro architekta (např. 'Kam bys mi doporučil dát gauč?')..."
          disabled={isLoading}
          style={inputStyle}
        />
        <button
          onClick={handleSend}
          disabled={isLoading || !inputText.trim()}
          style={sendBtnStyle(isLoading || !inputText.trim())}
        >
          {isLoading ? '...' : 'Odeslat'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------- Styles -----------------------------------

const cardStyle: React.CSSProperties = {
  backgroundColor: '#1e1e1e',
  border: '1px solid #333333',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  height: '750px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden'
};

const headerStyle: React.CSSProperties = {
  padding: '14px 18px',
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
  borderBottom: '1px solid #333333',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

const avatarStyle: React.CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: 'rgba(156, 39, 176, 0.2)',
  border: '1px solid rgba(156, 39, 176, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px'
};

const skillsBtnStyle: React.CSSProperties = {
  backgroundColor: 'rgba(156, 39, 176, 0.15)',
  border: '1px solid rgba(156, 39, 176, 0.4)',
  color: '#e1bee7',
  fontSize: '12px',
  fontWeight: 500,
  padding: '4px 10px',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

const clearBtnStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #444',
  color: '#9ca3af',
  fontSize: '12px',
  padding: '4px 10px',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

const modelSelectorContainerStyle: React.CSSProperties = {
  padding: '8px 18px',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  borderBottom: '1px solid #2a2a2a',
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
};

const modelSelectStyle: React.CSSProperties = {
  backgroundColor: '#121212',
  color: '#f3f4f6',
  border: '1px solid #333',
  borderRadius: '6px',
  padding: '4px 8px',
  fontSize: '12px',
  outline: 'none',
  flex: 1
};

const messagesScrollStyle: React.CSSProperties = {
  flex: 1,
  padding: '16px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column'
};

const userBubbleStyle: React.CSSProperties = {
  backgroundColor: '#9c27b0',
  color: '#ffffff',
  padding: '10px 14px',
  borderRadius: '14px 14px 2px 14px',
  maxWidth: '85%',
  fontSize: '13px',
  boxShadow: '0 2px 8px rgba(156, 39, 176, 0.2)'
};

const architectBubbleStyle: React.CSSProperties = {
  backgroundColor: '#2a2a2a',
  color: '#f3f4f6',
  padding: '12px 16px',
  borderRadius: '14px 14px 14px 2px',
  maxWidth: '90%',
  fontSize: '13px',
  border: '1px solid #383838',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
};

const handoffCardStyle: React.CSSProperties = {
  marginTop: '12px',
  padding: '12px',
  backgroundColor: 'rgba(156, 39, 176, 0.1)',
  border: '1px solid rgba(156, 39, 176, 0.3)',
  borderRadius: '8px',
  animation: 'pulseGlow 3s infinite'
};

const handoffHeaderStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#ab47bc',
  marginBottom: '6px'
};

const handoffPromptBoxStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '12px',
  backgroundColor: '#121212',
  padding: '8px 10px',
  borderRadius: '6px',
  color: '#4ade80',
  border: '1px solid #282828',
  lineHeight: '1.4'
};

const runOrchestratorBtnStyle: React.CSSProperties = {
  backgroundColor: '#9c27b0',
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 14px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  flex: 1,
  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
};

const copyPromptBtnStyle: React.CSSProperties = {
  backgroundColor: '#333333',
  color: '#e5e7eb',
  border: '1px solid #444444',
  borderRadius: '6px',
  padding: '8px 12px',
  fontSize: '12px',
  cursor: 'pointer'
};

const inputContainerStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderTop: '1px solid #333333',
  backgroundColor: 'rgba(255, 255, 255, 0.01)',
  display: 'flex',
  gap: '10px'
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  backgroundColor: '#121212',
  border: '1px solid #333',
  borderRadius: '8px',
  padding: '10px 14px',
  color: '#f3f4f6',
  fontSize: '13px',
  outline: 'none'
};

const sendBtnStyle = (disabled: boolean): React.CSSProperties => ({
  backgroundColor: disabled ? '#333333' : '#9c27b0',
  color: disabled ? '#888888' : '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '0 18px',
  fontWeight: 600,
  fontSize: '13px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'background-color 0.2s'
});

const errorStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  borderRadius: '6px',
  color: '#fca5a5',
  fontSize: '12px',
  marginTop: '8px'
};

const dotStyle = (delayMs: number): React.CSSProperties => ({
  display: 'inline-block',
  fontSize: '16px',
  animation: 'chatDotBounce 1.4s infinite ease-in-out both',
  animationDelay: `${delayMs}ms`
});
